import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PageLayout from './PageLayout';
import { getHabitConsistency } from '../utils/habitUtils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NoteIcon from '@mui/icons-material/Note';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalculateIcon from '@mui/icons-material/Calculate';

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100',
};

const PILLAR_META = {
  vitalidade: {
    title: 'Vitalidade',
    subtitle: 'Combustível',
    premise: 'Aqui você organiza exercícios, sono, alimentação, hidratação e tudo que recarrega sua energia física.',
    shortcuts: [
      { id: 'sleep', label: 'Sono', icon: <BedtimeIcon /> },
      { id: 'treinos', label: 'Treinos', icon: <FitnessCenterIcon /> },
      { id: 'nutricao', label: 'Nutrição', icon: <RestaurantIcon /> },
    ],
  },
  mentalidade: {
    title: 'Mentalidade',
    subtitle: 'Controle',
    premise: 'Organize meditações, dicas de livros, journaling, práticas de foco e tudo que fortalece seu estado emocional.',
    shortcuts: [
      { id: 'meditations', label: 'Meditações', icon: <SelfImprovementIcon /> },
      { id: 'livros', label: 'Dicas de Livros', icon: <MenuBookIcon /> },
      { id: 'estudos', label: 'Estudos', icon: <SchoolIcon /> },
    ],
  },
  prosperidade: {
    title: 'Prosperidade',
    subtitle: 'Alicerce',
    premise: 'Acompanhe finanças, carreira, projetos lucrativos e estrutura profissional.',
    shortcuts: [
      { id: 'orcamento', label: 'Orçamento Pessoal', icon: <AccountBalanceWalletIcon /> },
      { id: 'investimentos', label: 'Investimentos', icon: <TrendingUpIcon /> },
      { id: 'simulador', label: 'Simulador', icon: <CalculateIcon /> },
    ],
  },
};

function normalizePillar(pillarId) {
  return pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade');
}

export default function PilarLayout({
  pillarId,
  habits = [],
  goals = [],
  challenges = [],
  notes: notesProp = [],
  customColors,
  onNavigate,
  loadNotesFromFirestore = true,
}) {
  const { currentUser } = useAuth();
  const [notesLocal, setNotesLocal] = useState([]);

  const pid = normalizePillar(pillarId);
  const meta = PILLAR_META[pid] || PILLAR_META.vitalidade;
  const primaryColor = customColors?.pillars?.[pid] || DEFAULT_PILLAR_COLORS[pid] || DEFAULT_PILLAR_COLORS.vitalidade;

  const habitsForPillar = useMemo(
    () => (habits || []).filter((h) => normalizePillar(h.pillar) === pid && h.active !== false),
    [habits, pid]
  );
  const goalsForPillar = useMemo(
    () => (goals || []).filter((g) => normalizePillar(g.pillar) === pid),
    [goals, pid]
  );
  const challengesForPillar = useMemo(
    () => (challenges || []).filter((c) => normalizePillar(c.pillar) === pid),
    [challenges, pid]
  );

  const notesForPillar = useMemo(() => {
    const source = loadNotesFromFirestore ? notesLocal : (notesProp || []);
    return source.filter((n) => normalizePillar(n.pillar) === pid);
  }, [loadNotesFromFirestore, notesLocal, notesProp, pid]);

  useEffect(() => {
    if (!loadNotesFromFirestore || !currentUser?.uid) return;
    const load = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          const allNotes = (data.notes || []).filter((n) => normalizePillar(n.pillar) === pid);
          setNotesLocal(allNotes);
        }
      } catch (err) {
        console.error('Erro ao carregar notas do pilar:', err);
      }
    };
    load();
  }, [currentUser?.uid, pid, loadNotesFromFirestore]);

  const insights = useMemo(() => {
    const withRate = habitsForPillar
      .map((h) => ({ habit: h, ...getHabitConsistency(h) }))
      .filter((x) => x.scheduled > 0);
    const sorted = [...withRate].sort((a, b) => b.rate - a.rate);
    const most = sorted[0];
    const least = sorted[sorted.length - 1];
    return { most, least };
  }, [habitsForPillar]);

  const recentActivities = useMemo(() => {
    const items = [];
    const now = new Date();
    const cutOff = new Date(now);
    cutOff.setDate(cutOff.getDate() - 14);

    habitsForPillar.forEach((h) => {
      (h.completedDates || []).forEach((d) => {
        const dateStr = typeof d === 'string' ? d.split('T')[0] : d;
        const date = new Date(dateStr + 'T12:00:00');
        if (date >= cutOff) {
          items.push({ type: 'habit', date, label: `${h.name} concluído`, ref: h });
        }
      });
    });

    goalsForPillar.forEach((g) => {
      const updated = g.updatedAt ? new Date(g.updatedAt) : (g.createdAt ? new Date(g.createdAt) : null);
      if (updated && updated >= cutOff) {
        items.push({ type: 'goal', date: updated, label: `Meta atualizada: ${g.title}`, ref: g });
      }
    });

    notesForPillar.forEach((n) => {
      const created = n.createdAt ? new Date(n.createdAt) : (n.date ? new Date(n.date) : null);
      if (created && created >= cutOff) {
        const title = (n.title || 'Anotação').slice(0, 30);
        items.push({ type: 'note', date: created, label: `Nota: ${title}${(n.title || '').length > 30 ? '…' : ''}`, ref: n });
      }
    });

    challengesForPillar.forEach((c) => {
      const start = c.startDate ? new Date(c.startDate + 'T12:00:00') : (c.createdAt ? new Date(c.createdAt) : null);
      if (start && start >= cutOff) {
        items.push({ type: 'challenge', date: start, label: `Desafio iniciado: ${c.name}`, ref: c });
      }
    });

    items.sort((a, b) => b.date - a.date);
    return items.slice(0, 12);
  }, [habitsForPillar, goalsForPillar, notesForPillar, challengesForPillar]);

  const handleNavigate = (tabId) => {
    if (typeof onNavigate === 'function') onNavigate(tabId);
  };

  const statCards = [
    { label: 'Hábitos ativos', value: habitsForPillar.length, icon: <CheckCircleIcon /> },
    { label: 'Metas', value: goalsForPillar.length, icon: <FlagIcon /> },
    { label: 'Desafios', value: challengesForPillar.length, icon: <EmojiEventsIcon /> },
    { label: 'Anotações', value: notesForPillar.length, icon: <NoteIcon /> },
  ];

  return (
    <PageLayout title={`${meta.title} (${meta.subtitle})`} hideAddButton customColors={customColors}>
      <Box sx={{ maxWidth: 960, mx: 'auto', pb: 4 }}>
        {/* Cabeçalho dinâmico */}
        <Card
          sx={{
            mb: 3,
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.12)} 0%, ${alpha(primaryColor, 0.04)} 100%)`,
            borderLeft: `4px solid ${primaryColor}`,
          }}
        >
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor, mb: 0.5 }}>
              {meta.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {meta.premise}
            </Typography>
          </CardContent>
        </Card>

        {/* Barra de Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((stat) => (
            <Grid item xs={6} sm={3} key={stat.label}>
              <Card
                sx={{
                  height: '100%',
                  borderTop: `3px solid ${primaryColor}`,
                  bgcolor: alpha(primaryColor, 0.06),
                }}
              >
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: primaryColor }}>{stat.icon}</Box>
                    <Typography variant="caption" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>
                    {stat.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Atalhos do pilar */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
              Acesso rápido
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {meta.shortcuts.map((s) => (
                <Button
                  key={s.id}
                  variant="outlined"
                  startIcon={s.icon}
                  onClick={() => handleNavigate(s.id)}
                  sx={{ borderColor: primaryColor, color: primaryColor }}
                >
                  {s.label}
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Insights */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>
                  Insights
                </Typography>
                {habitsForPillar.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum hábito ativo neste pilar.
                  </Typography>
                ) : insights.most ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon sx={{ color: 'success.main' }} fontSize="small" />
                      <Typography variant="body2">
                        <strong>Maior consistência:</strong> {insights.most.habit.name} ({Math.round(insights.most.rate * 100)}% nos últimos 30 dias)
                      </Typography>
                    </Box>
                    {insights.least && insights.least.habit.id !== insights.most.habit.id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDownIcon sx={{ color: 'warning.main' }} fontSize="small" />
                        <Typography variant="body2">
                          <strong>Menor consistência:</strong> {insights.least.habit.name} ({Math.round(insights.least.rate * 100)}% nos últimos 30 dias)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Registre conclusões dos hábitos para ver a consistência.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Atividades recentes */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>
                  Atividades recentes
                </Typography>
                {recentActivities.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma atividade recente neste pilar.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {recentActivities.map((act, idx) => (
                      <ListItem key={`${act.type}-${idx}-${act.date.getTime()}`} disablePadding sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28, color: primaryColor }}>
                          {act.type === 'habit' && <CheckCircleIcon fontSize="small" />}
                          {act.type === 'goal' && <FlagIcon fontSize="small" />}
                          {act.type === 'note' && <NoteIcon fontSize="small" />}
                          {act.type === 'challenge' && <EmojiEventsIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={act.label}
                          secondary={format(act.date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </PageLayout>
  );
}
